<template>
  <!-- Install Banner (Non-iOS) -->
  <Teleport to="body">
    <Transition name="banner">
      <div
        v-if="showInstallBanner && !isIOS"
        class="fixed top-6 right-6 z-[9999] max-w-sm"
      >
        <div
          class="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 backdrop-blur-sm bg-opacity-95"
        >
          <!-- Gradient Header -->
          <div
            class="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
          ></div>

          <div class="p-5">
            <div class="flex items-start gap-4">
              <!-- Icon -->
              <div
                class="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center"
              >
                <DownloadCloud class="w-6 h-6 text-blue-600" />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-gray-900 text-sm mb-1">
                  Install App
                </h3>
                <p class="text-xs text-gray-600 leading-relaxed">
                  Add this app to your home screen for instant access
                </p>
              </div>

              <!-- Close Button -->
              <button
                @click="closeBanner"
                class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Close install prompt"
              >
                <X class="w-5 h-5" />
              </button>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 mt-4">
              <button
                @click="installApp"
                :disabled="isInstalling"
                class="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-2.5 rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                <span v-if="isInstalling">
                  <Loader class="w-4 h-4 animate-spin" />
                </span>
                <span v-else>
                  <Download class="w-4 h-4" />
                </span>
                {{ isInstalling ? "Installing..." : "Install" }}
              </button>
              <button
                @click="closeBanner"
                class="px-4 py-2.5 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors text-sm border"
              >
                Later
              </button>
            </div>

            <!-- Do not show again checkbox -->
            <label
              class="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 cursor-pointer"
            >
              <input
                type="checkbox"
                v-model="doNotShowAgainAndroid"
                class="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
              />
              <span class="text-xs text-gray-600">Do not show again</span>
            </label>
          </div>
        </div>
      </div>
    </Transition>

    <!-- iOS Install Banner -->
    <Transition name="banner">
      <div
        v-if="showInstallBanner && isIOS"
        class="fixed top-6 right-6 z-[9999] max-w-sm"
      >
        <div
          class="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 backdrop-blur-sm bg-opacity-95"
        >
          <!-- Gradient Header -->
          <div
            class="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
          ></div>

          <div class="p-5">
            <div class="flex items-start gap-4">
              <!-- Icon -->
              <div
                class="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center"
              >
                <Share2 class="w-6 h-6 text-blue-600" />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-gray-900 text-sm mb-1">
                  Add to Home Screen
                </h3>
                <p class="text-xs text-gray-600 leading-relaxed">
                  Get quick access to this app anytime
                </p>
              </div>

              <button
                @click="closeBanner"
                class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Close install prompt"
              >
                <X class="w-5 h-5" />
              </button>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 mt-4">
              <button
                @click="showIOSGuide = true"
                class="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-2.5 rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 flex items-center justify-center gap-2 text-sm"
              >
                <HelpCircle class="w-4 h-4" />
                How to?
              </button>
              <button
                @click="closeBanner"
                class="px-4 py-2.5 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                Dismiss
              </button>
            </div>

            <!-- Do not show again -->
            <label
              class="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 cursor-pointer"
            >
              <input
                type="checkbox"
                v-model="doNotShowAgainIOS"
                class="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
              />
              <span class="text-xs text-gray-600">Do not show again</span>
            </label>
          </div>
        </div>
      </div>
    </Transition>

    <!-- iOS Installation Guide Modal -->
    <Transition name="modal">
      <div
        v-if="showIOSGuide && isIOS"
        class="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-[10000] p-4 backdrop-blur-sm"
        @click.self="showIOSGuide = false"
      >
        <Transition name="slide-up">
          <div
            v-show="showIOSGuide"
            class="bg-white rounded-3xl md:rounded-2xl w-full md:max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            <!-- Header -->
            <div
              class="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex items-center justify-between rounded-t-3xl"
            >
              <div class="flex items-center gap-3">
                <BookOpen class="w-6 h-6" />
                <h2 class="font-bold text-lg">Installation Guide</h2>
              </div>
              <button
                @click="showIOSGuide = false"
                class="text-white/80 hover:text-white transition-colors p-2"
              >
                <X class="w-5 h-5" />
              </button>
            </div>

            <!-- Updated Steps -->
            <div class="p-6 space-y-4">
              <div
                v-for="(step, index) in iosSteps"
                :key="index"
                class="flex items-start gap-4 p-4 rounded-xl border bg-white shadow-sm"
                :class="
                  index === iosSteps.length - 1
                    ? 'bg-green-50 border-green-200'
                    : 'bg-blue-50 border-blue-200'
                "
              >
                <!-- Icon -->
                <div
                  class="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white"
                  :class="
                    index === iosSteps.length - 1
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                      : 'bg-gradient-to-br from-blue-500 to-purple-600'
                  "
                >
                  <component
                    :is="step.icon"
                    class="w-5 h-5"
                  />
                </div>

                <!-- Text -->
                <div class="flex-1">
                  <h3 class="font-semibold text-gray-900 text-sm">
                    {{ step.number }}. {{ step.title }}
                  </h3>
                  <p class="text-xs text-gray-700 mt-1 leading-relaxed">
                    {{ step.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
  import { ref, watch, onMounted, onBeforeUnmount } from "vue";
  import {
    DownloadCloud,
    Download,
    X,
    Loader,
    Share2,
    HelpCircle,
    BookOpen,
    ChevronDown,
    PlusSquare,
    Check,
    CheckCircle,
  } from "lucide-vue-next";

  // =====================
  // Refs
  // =====================
  const deferredPrompt = ref(null);
  const showInstallBanner = ref(false);
  const showIOSGuide = ref(false);
  const isIOS = ref(false);
  const isInstalling = ref(false);
  const doNotShowAgainAndroid = ref(false);
  const doNotShowAgainIOS = ref(false);
  const isMobileOrTablet = ref(false);

  // =====================
  // iOS Steps
  // =====================
  const iosSteps = [
    {
      number: "1",
      title: "Open Share Menu",
      description: "Tap the Share icon in the Safari toolbar.",
      icon: Share2,
    },
    {
      number: "2",
      title: "Scroll Down",
      description: "Scroll down to reveal more options.",
      icon: ChevronDown,
    },
    {
      number: "3",
      title: 'Tap "Add to Home Screen"',
      description: "Choose the ‘Add to Home Screen’ option.",
      icon: PlusSquare,
    },
    {
      number: "4",
      title: "Confirm & Add",
      description: "Tap ‘Add’ at the top right to confirm.",
      icon: Check,
    },
    {
      number: "✓",
      title: "All Set!",
      description: "Your app is now added to your home screen.",
      icon: CheckCircle,
    },
  ];

  // =====================
  // Helpers
  // =====================
  const detectIOS = () => {
    const ua = navigator.userAgent.toLowerCase();
    isIOS.value = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
  };

  const detectScreenSize = () => {
    isMobileOrTablet.value = window.innerWidth <= 1024; // mobile + tablet
  };

  const getAndroidDoNotShowAgain = () =>
    localStorage.getItem("pwa-android-do-not-show") === "true";

  const getIOSDoNotShowAgain = () =>
    localStorage.getItem("pwa-ios-do-not-show") === "true";

  // =====================
  // Watchers
  // =====================
  watch(doNotShowAgainAndroid, (val) => {
    val
      ? localStorage.setItem("pwa-android-do-not-show", "true")
      : localStorage.removeItem("pwa-android-do-not-show");
  });

  watch(doNotShowAgainIOS, (val) => {
    val
      ? localStorage.setItem("pwa-ios-do-not-show", "true")
      : localStorage.removeItem("pwa-ios-do-not-show");
  });

  // 🔥 Auto-hide banner if resized to desktop
  watch(isMobileOrTablet, (val) => {
    if (!val) {
      showInstallBanner.value = false;
      showIOSGuide.value = false;
    }
  });

  // =====================
  // Banner controls
  // =====================
  const closeBanner = () => {
    showInstallBanner.value = false;
  };

  const installApp = async () => {
    if (!deferredPrompt.value) return;

    isInstalling.value = true;
    try {
      deferredPrompt.value.prompt();
      const { outcome } = await deferredPrompt.value.userChoice;

      if (outcome === "accepted") {
        showInstallBanner.value = false;
        localStorage.setItem("pwa-installed", "true");
      }

      deferredPrompt.value = null;
    } catch (err) {
      console.error("Installation failed:", err);
    } finally {
      isInstalling.value = false;
    }
  };

  // =====================
  // Event handlers
  // =====================
  const handleBeforeInstallPrompt = (e) => {
    e.preventDefault();
    deferredPrompt.value = e;
    showInstallBanner.value = true;
  };

  const handleAppInstalled = () => {
    showInstallBanner.value = false;
    localStorage.setItem("pwa-installed", "true");
  };

  // =====================
  // Init
  // =====================
  onMounted(() => {
    detectIOS();
    detectScreenSize();

    // ❌ Never show on desktop/laptop
    if (!isMobileOrTablet.value) return;

    // Already installed
    if (localStorage.getItem("pwa-installed") === "true") return;

    // Do-not-show flags
    if (isIOS.value && getIOSDoNotShowAgain()) {
      doNotShowAgainIOS.value = true;
      return;
    }
    if (!isIOS.value && getAndroidDoNotShowAgain()) {
      doNotShowAgainAndroid.value = true;
      return;
    }

    if (!isIOS.value) {
      window.addEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
        { once: true },
      );
    } else {
      showInstallBanner.value = true;
    }

    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("resize", detectScreenSize);
  });

  // =====================
  // Cleanup
  // =====================
  onBeforeUnmount(() => {
    window.removeEventListener("resize", detectScreenSize);
    window.removeEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt,
    );
    window.removeEventListener("appinstalled", handleAppInstalled);
  });
</script>

<style scoped>
  .banner-enter-active,
  .banner-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .banner-enter-from,
  .banner-leave-to {
    opacity: 0;
    transform: translateY(-16px) translateX(20px);
  }
  .modal-enter-active,
  .modal-leave-active {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .modal-enter-from,
  .modal-leave-to {
    opacity: 0;
  }
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .slide-up-enter-from,
  .slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }
  @media (max-width: 768px) {
    .slide-up-enter-from,
    .slide-up-leave-to {
      transform: translateY(100%);
    }
  }
  .ml-13 {
    margin-left: 3.25rem;
  }
</style>
